import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, Eyebrow } from "./shared";
import { resolveUrl } from "@/lib/api";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  const target = Number(value) || 0;

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const dur = 1800;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
      else setN(target);
    };
    requestAnimationFrame(step);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {n.toLocaleString("id-ID")}
      {suffix}
    </span>
  );
};

export const TrackRecord = ({ stats = [], partners = [] }) => {
  const row = [...partners, ...partners];
  return (
    <section id="experience" data-testid="experience-section" className="relative py-24 md:py-32">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] radial-glow-gold z-0" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex justify-center">
              <Eyebrow testid="experience-eyebrow">Track Record</Eyebrow>
            </div>
            <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight leading-tight">
              Rekam jejak yang <span className="text-gradient-gold">terbukti</span>.
            </h2>
            <p className="mt-5 text-slate-400">Angka yang mewakili komitmen kami terhadap kualitas dan hasil nyata.</p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.id || i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`stat-${i}`}
              className="rounded-2xl border border-white/10 bg-navy-card/50 p-8 text-center hover:border-gold/40 transition-colors duration-300"
            >
              <div className="font-heading font-black text-4xl md:text-5xl text-cyan tracking-tight">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-sm text-slate-400 font-body">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-20">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-slate-500 mb-8">Dipercaya oleh instansi & perusahaan mitra</p>
          <div className="border-y border-white/10 py-8 overflow-hidden mask-fade-x">
            <div className="flex w-max animate-marquee items-center gap-14">
              {row.map((p, i) => (
                <span key={i} className="shrink-0 grid place-items-center">
                  {p.logo_url ? (
                    <img src={resolveUrl(p.logo_url)} alt={p.name} className="h-9 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity duration-300 grayscale hover:grayscale-0" />
                  ) : (
                    <span className="font-heading font-bold text-xl tracking-widest text-slate-600 hover:text-white transition-colors duration-300">{p.name}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
